import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const CallToAction = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings`)
      .then(res => res.json())
      .then(data => {
        setSettings(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching CTA settings:', err);
        setLoading(false);
      });
  }, []);

  const isAvailable = settings?.availability_status === 'available';

  return (
    <section className="py-20 bg-background relative overflow-hidden border-t border-white/5">
      {/* Background Pattern & Glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-20 w-[500px] h-[500px] bg-primary-900/20 rounded-full blur-[120px]"></div>
      </div>
      
      <div className="absolute inset-0 bg-white/[0.02] backdrop-blur-[2px]"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center">
          {/* Main CTA Content */}
          <div className="max-w-3xl mx-auto mb-12">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl text-text-primary mb-6">
              Ready to Collaborate on a 
              <span className="text-accent"> Research or Outreach Initiative? </span>
            </h2>
            <p className="text-lg lg:text-xl text-text-secondary leading-relaxed mb-8 max-w-2xl mx-auto">
              Whether you're from a research lab, institution, school, or STEM initiative—let’s 
              connect to drive meaningful impact in science, education, or technology.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button 
              variant="secondary" 
              size="xl"
              iconName="MessageCircle" 
              iconPosition="left"
            >
              <Link 
                to="/contact" 
                className="flex items-center"
                title="Open the Contact & Inquiries Form"
                aria-label="Navigate to the Contact Page to propose a collaboration"
              >
                Propose a Collaboration
              </Link>
            </Button>
              <a
                href={settings?.calendly_url || "https://calendly.com/sudhan-r-web/30min"}
                target="_blank"
                rel="noopener noreferrer"
                title="Book a Discovery Call via Calendly"
                aria-label="Schedule a 30-minute discussion on Calendly"
              >
                <Button 
                variant="outline" 
                size="xl"
                iconName="Calendar" 
                iconPosition="left"
                className="bg-white/5 text-text-primary border-white/20 hover:bg-white/10 shadow-xl"
                >
                   Book a Discussion
                </Button>
              </a>
          </div>

          {/* Contact Options */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-8">
            {/* Email Card - Cyan Theme */}
            <div className="text-center group bg-surface/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-500/40 rounded-2xl p-8 shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 relative overflow-hidden bg-gradient-to-br from-blue-500/5 to-transparent">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16 blur-2xl opacity-20 bg-blue-500"></div>
              <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-5 border border-white/5 group-hover:bg-blue-500/10 transition-smooth">
                <Icon name="Mail" size={24} className="text-blue-400" />
              </div>
              <h3 className="font-heading font-semibold text-text-primary mb-2 text-lg">Email Me</h3>
              <p className="text-text-secondary text-sm mb-4 min-h-[40px]">
                Get a detailed response within 24 hours
              </p>
              <a 
                href={`mailto:${settings?.contact_email || 'sudhan.r.web@gmail.com'}`} 
                title={`Email ${settings?.contact_email || 'sudhan.r.web@gmail.com'}`}
                aria-label={`Send an email to ${settings?.contact_email || 'sudhan.r.web@gmail.com'}`}
                className="inline-block px-4 py-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 font-medium rounded-lg transition-smooth break-all text-sm border border-blue-500/20"
              >
                {settings?.contact_email || 'sudhan.r.web@gmail.com'}
              </a>
            </div>

            {/* Phone Card - Green Theme */}
            <div className="text-center group bg-surface/50 backdrop-blur-xl border border-green-500/20 hover:border-green-500/40 rounded-2xl p-8 shadow-2xl hover:shadow-green-500/10 transition-all duration-300 relative overflow-hidden bg-gradient-to-br from-green-500/5 to-transparent">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16 blur-2xl opacity-20 bg-green-500"></div>
              <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-5 border border-white/5 group-hover:bg-green-500/10 transition-smooth">
                <Icon name="Phone" size={24} className="text-green-400" />
              </div>
              <h3 className="font-heading font-semibold text-text-primary mb-2 text-lg">Call Me</h3>
              <p className="text-text-secondary text-sm mb-4 min-h-[40px]">
                For academic inquiries only
              </p>
              <a 
                href="tel:+919940980978" 
                title="Call Sudhan R (Academic Inquiries Only)"
                aria-label="Call +91 99409 80978"
                className="inline-block px-4 py-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 font-medium rounded-lg transition-smooth whitespace-nowrap text-sm border border-green-500/20"
              >
                +91 99409 80978
              </a>
            </div>

            {/* Socials Card - Purple Theme */}
            <div className="text-center group bg-surface/50 backdrop-blur-xl border border-purple-500/20 hover:border-purple-500/40 rounded-2xl p-8 shadow-2xl hover:shadow-purple-500/10 transition-all duration-300 relative overflow-hidden bg-gradient-to-br from-purple-500/5 to-transparent">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16 blur-2xl opacity-20 bg-purple-500"></div>
              <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-5 border border-white/5 group-hover:bg-purple-500/10 transition-smooth">
                <Icon name="MessageSquare" size={24} className="text-purple-400" />
              </div>
              <h3 className="font-heading font-semibold text-text-primary mb-2 text-lg">Let's Chat</h3>
              <p className="text-text-secondary text-sm mb-4 min-h-[40px]">
                Connect on your preferred platform
              </p>
              <div className="flex justify-center items-center gap-3">
                <a 
                  href="https://www.linkedin.com/in/sudhan23082004/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  title="Connect with Sudhan on LinkedIn"
                  aria-label="Visit Sudhan's LinkedIn Profile"
                  className="px-4 py-2 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 font-medium rounded-lg transition-smooth flex items-center gap-2 text-sm border border-purple-500/20"
                >
                  <Icon name="Linkedin" size={16} /> LinkedIn
                </a>
                <span className="text-text-muted">•</span>
                <a 
                  href="https://instagram.com/sudhan-r" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  title="Follow Sudhan on Instagram"
                  aria-label="Visit Sudhan's Instagram Profile"
                  className="px-4 py-2 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 font-medium rounded-lg transition-smooth flex items-center gap-2 text-sm border border-purple-500/20"
                >
                  <Icon name="Instagram" size={16} /> Instagram
                </a>
              </div>
            </div>
          </div>

          {/* Availability Status */}
          <div className="mt-16 p-4 bg-white/5 backdrop-blur-md rounded-xl inline-flex items-center gap-3 border border-white/10 shadow-2xl">
            <div className={`w-3 h-3 rounded-full animate-pulse shadow-lg ${isAvailable ? 'bg-success shadow-success/50' : 'bg-warning shadow-warning/50'}`}></div>
            <span className="text-text-primary font-medium tracking-wide">
              {loading ? 'Consulting Status...' : (isAvailable ? 'Actively accepting new research & tech collaborations' : 'Currently focused on Research Missions')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;